
import { defineComponent, nextTick, computed } from 'vue';
import { mdiArrowCollapseLeft, mdiArrowCollapseRight, mdiChevronRight, mdiHelp, mdiChevronDown, mdiAccount, mdiBullhorn } from '@mdi/js';
import UserState, { IUserState } from './store/modules/users';
import { IHubSpotToken, IUser } from './store/interfaces/IUser';
import { IOrganization } from './store/interfaces/IOrganization';
import { getModule } from 'vuex-module-decorators';
import ExpiredSubscrption from './components/ExpiredSubscrption.vue';
import Onboarding from './components/Onboarding.vue';

import OnboardStatusState, { IOnboardStatusState } from './store/modules/onboarding';
import RoleState, { IRoleState } from './store/modules/role';
import Icon from './components/Icon.vue';

let onboardStatusState = {} as IOnboardStatusState;
let roleState = {} as IRoleState;
let userState = {} as IUserState;

export default defineComponent({
    components: { 
      ExpiredSubscrption,
      Onboarding,
      Icon
    },
    name: 'Settings Portal',

    data() {
        return {
            appBooting: true,
            appBootLogo: require('./assets/images/signup-v2/ditto-mark-white.svg'),
            mdiArrowCollapseLeft,
            mdiArrowCollapseRight,
            mdiChevronRight,
            mdiHelp,
            mdiBullhorn,
            mdiAccount,
            mdiChevronDown,
            navCollapsed: false,
            orgTooltipDisabled: false,
            organizationSubNavExpanded: false,
            signageSubNavExpanded: false,
            org: {} as IOrganization,
            resizeEventThrottle: false,
            windowWidth: 0,
            mobileNavToggled: false,
            shadowedNav: false,
            sideNavReference: {} as any,
            accountPopoverEnabled: false as boolean,
            organizationPopoverEnabled: false as boolean,
            signagePopoverEnabled: false as boolean,
            hideOnboardingOnMobile: true as boolean,
            hideOnboardingByAccess: true as boolean,
            hasReceiversAccess: true as boolean,
            hasRoomsAccess: true as boolean,
            hasLocationsAccess: true as boolean,
            hasSignageAccess: true as boolean,
            hasSignageLayoutAccess: true as boolean,
            hasAlertsAccess: true as boolean,
            hasOrganizationAccess: true as boolean,
            hasBillingAccess: true as boolean,
            hasUsersAccess: true as boolean,
            hasSuperAccess: false as boolean
        }
    },
  
    computed: {

        steps() {
            return onboardStatusState.stepInstructions
        },

        currentStep() {
            return onboardStatusState.currentStep;
        },        

        currentStepNumber() {
            let progressStepNumber;
            switch (onboardStatusState.currentStep.category) {
                case 'Overview':
                    progressStepNumber = 1;
                    break;

                 case 'Receivers':
                    progressStepNumber = 2;
                    break;

                 case 'Rooms':
                    progressStepNumber = 3;
                    break;

                 case 'Finish':
                    progressStepNumber = 4;
                    break;
            
                default:
                    progressStepNumber = 1;
                    break;
            }
            return progressStepNumber;
        },

        userOnboardStatusProp() {
            return onboardStatusState.userOnboardStatus;
        },

        me(): IUser | undefined {
            return userState.me;
        },

        hubspotToken(): IHubSpotToken | undefined {
            return userState.hubspotToken;
        },

        isAdmin(): boolean {
            return roleState.isAdmin;
        },

        navCollapsedAuto(): boolean {
            return this.navCollapsed || this.windowWidth < 992;
        },

        roomSignMode (): any {
            return this.$route.path.includes('background') || this.$route.path.includes('instructions');
        },

        orgName (): string | null {
            if(!this.me || !this.me.organization) return null;
            return this.me.organization.name;
        },

        // FLOCK-6612: true only on authenticated app routes. noAuth routes are
        // the bare signage template pages, which must render nothing but the
        // template itself.
        showAppChrome(): boolean {
            return !!(this.$route && this.$route.meta && !this.$route.meta.noAuth);
        },

        signupMode(): any {
            if(this.$route.path.includes('signup')) return true;
            if(this.$route.path.includes('error')) return true;
            // if(this.$route.path.includes('template')) return true;
            if(this.roomSignMode) return false;
            return false;
        },

        showExpiredSubscriptionModal (): boolean {
            // Don't show expired subscription modal on user swap page 
            if(this.$route.path.includes('users/swap')) {
                return false;
            }

            if(!this.$route.path.includes('organization/billing')) {
                return true;
            }
    
            return false;
        },

        currentHost(): string {
            return `${window.location.protocol}//${window.location.hostname}/`;
        }
    },
    
    provide() {
        return {
            windowWidth: computed(() => this.windowWidth),
            currentStep: computed(() => this.currentStep),
            userOnboardStatus: computed(() => this.userOnboardStatusProp),
            hideOnboardingOnMobile: computed(() => this.hideOnboardingOnMobile),
            hideOnboardingByAccess: computed(() => this.hideOnboardingByAccess)
        }
    },

    async created() {

        // Window resize events for collapsed side-nav & sub-navs
        this.windowWidth = window.innerWidth;
        window.addEventListener("resize", this.resizeEventHandler);
        // Window resize events for collapsed side-nav & sub-navs
        this.navCollapsed = window.localStorage.getItem('dt-nav-collapse') == "true" ? true : false;
        this.organizationSubNavExpanded = window.localStorage.getItem('dt-org-subnav-expanded') == "true" ? true : false;
        this.signageSubNavExpanded = window.localStorage.getItem('dt-signage-subnav-expanded') == "true" ? true : false;

        if(!this.signupMode) {
            try {
                userState = getModule(UserState);
                onboardStatusState = getModule(OnboardStatusState);
                roleState = getModule(RoleState);

                const user = await userState.fetchMe(null);
                
                if(user.success) {
                    await Promise.all([
                        onboardStatusState.fetchOnboardStatus(),
                        roleState.fetchUserRole()
                    ]);
                    if(userState.me) {
                        this.org = userState.me.organization;
                    }
                    
                    this.hasSuperAccess = roleState.hasSuperAccess;
                    this.hasReceiversAccess = roleState.hasReceiversAccess;
                    this.hasRoomsAccess = roleState.hasRoomsAccess;
                    this.hasLocationsAccess = roleState.hasLocationsAccess;
                    this.hasSignageAccess = roleState.hasSignageAccess;
                    this.hasAlertsAccess = roleState.hasAlertsAccess;
                    this.hasOrganizationAccess = roleState.hasOrganizationAccess;
                    this.hasBillingAccess = roleState.hasBillingAccess;
                    this.hasUsersAccess = roleState.hasUsersAccess;
                    this.hasSignageLayoutAccess = roleState.hasSignageLayoutAccess
                    this.hideOnboardingByAccess = roleState.hasSuperAccess || !(this.hasReceiversAccess && this.hasRoomsAccess);
                    if(this.hideOnboardingByAccess || userState?.me?.email.endsWith('+support@airsquirrels.com')) {
                        await onboardStatusState.hide();
                        await onboardStatusState.fetchOnboardStatus(true);
                    }
                }
            }
            catch(e:any) {
                // don't care. Its very possible were not auth'd and we don't have "me"
            }
        }
        
        // We need to await nextTick so the nav is actually rendered
        await nextTick;
        this.sideNavReference = (this as any).$refs['sideNav'];
        this.sideNavReference.addEventListener("scroll", this.subNavScrollHandler);
        this.updateNavShadow();

        if(!this.signupMode) {
            onboardStatusState = getModule(OnboardStatusState);
        }
    },

    mounted() {
        // Drop the boot screen once the router settles (the auth guards hold
        // initial navigation until the Auth0 client resolves). The timeout is a
        // backstop so a hung guard can never strand the overlay.
        const dismissBoot = () => setTimeout(() => { this.appBooting = false; }, 150);
        this.$router.isReady().then(dismissBoot, dismissBoot);
        setTimeout(() => { this.appBooting = false; }, 10000);

        this.initHubSpot();
  
        // Check token expiry every hour
        setInterval(() => {
            this.refreshHubSpotTokenIfNeeded()
        }, 11 * 60 * 60 * 1000)

        this.$nextTick(() => {
            this.toggleOnboardingOnScreenResize();

            window.addEventListener('resize', this.toggleOnboardingOnScreenResize);
        });
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.toggleOnboardingOnScreenResize);
    },

    methods: {
        async initHubSpot() {
            // Set loadImmediately to false before HubSpot loads
            (window as any).hsConversationsSettings = {
                ...((window as any).hsConversationsSettings || {}),
                loadImmediately: false,
            };

            // Check if user data is available
            if (!this.me || !this.me.email || !this.me.is_active) {
                setTimeout(() => this.initHubSpot(), 2000);
                return;
            }

           

            console.log('🎯 Initializing HubSpot with official method for:', this.me.email);

            try {
                // Step 1: Get HubSpot token from Vuex store
                console.log('📡 Requesting HubSpot token from store...');
                
                const tokenResponse = await userState.fetchHubSpotToken();

                if (!tokenResponse.success || !tokenResponse.data) {
                    throw new Error('Failed to get HubSpot token: ' + tokenResponse.reason);
                }

                console.log('✅ Received HubSpot token from store', tokenResponse);

                // Step 2: Set the identification settings
                (window as any).hsConversationsSettings = {
                    ...((window as any).hsConversationsSettings || {}),
                    identificationEmail: this.me.email,
                    identificationToken: tokenResponse.data.token,
                };

                console.log('🔧 🔴HubSpot identification settings configured', (window as any).hsConversationsSettings);

                // Step 3: Wait for HubSpot to be ready and load the widget
                this.waitForHubSpotAndLoad();

            } catch (error) {
                console.error('❌ Error setting up HubSpot identification:', error);
                
                // Fallback to basic method if token generation fails
                console.log('🔄 Falling back to basic identification method...');
                this.fallbackToBasicMethod();
            }
        },

        waitForHubSpotAndLoad() {
            const checkHubSpot = () => {
                if ((window as any).HubSpotConversations?.widget) {
                    console.log('📤 Loading HubSpot widget with identification...');
                    
                    try {
                        (window as any).HubSpotConversations.widget.load();
                                                
                        console.log('✅ HubSpot widget loaded with user identification');
                        
                        // Set up event listeners for verification
                        this.setupHubSpotEventListeners();
                        
                    } catch (error) {
                        console.error('❌ Error loading HubSpot widget:', error);
                        this.fallbackToBasicMethod();
                    }
                } else {
                    setTimeout(checkHubSpot, 500);
                }
            };
            checkHubSpot();
        },

        setupHubSpotEventListeners() {
            (window as any).hsConversationsOnReady = [
                (api: any) => {
                    console.log("🎯 HubSpot widget is ready with identification");

                    api.addEventListener("conversationStarted", (payload: any) => {
                    console.log("💬 Conversation started with identified user:", payload);
                    });

                    api.addEventListener("contactAssociated", (data: any) => {
                    console.log("👤 Contact associated in HubSpot:", data);
                    });
                },
            ];

        },

        fallbackToBasicMethod() {
            console.log('🔄 Using fallback identification method...');
            
            const checkHubSpot = () => {
                if (!userState.me) return;
                if ((window as any).HubSpotConversations?.widget) {
                    try {
                        const nameParts = userState.me.name ? userState.me.name.split(' ') : [];
                        
                        (window as any).HubSpotConversations.widget.refresh({
                            email: userState.me.email,
                            firstName: nameParts[0] || '',
                            lastName: nameParts.slice(1).join(' ') || '',
                        });
                        
                        (window as any).HubSpotConversations.widget.load();
                        
                        console.log('✅ HubSpot fallback method completed');
                        
                    } catch (error) {
                        console.error('❌ Fallback method also failed:', error);
                    }
                } else {
                    setTimeout(checkHubSpot, 500);
                }
            };
            checkHubSpot();
        },

        async refreshHubSpotTokenIfNeeded() {
            const token = userState.hubspotToken;
            
            if (!token || !userState.me?.email) {
                return;
            }

            // Check if token expires in the next hour
            const oneHour = 60 * 60 * 1000;
            const now = Date.now();
            
            if (now > (token.expiresAt - oneHour)) {
                console.log('🔄 Refreshing HubSpot token...');
                
                // Clear the old token and session data
                await userState.clearHubSpotToken();
                
                // Re-initialize HubSpot with new token
                setTimeout(() => {
                    this.initHubSpot();
                }, 1000);
            }
        },

        onStartOnboarding() {
            if(!this.hideOnboardingByAccess) {
                onboardStatusState.start();
            }
        },

        toggleOnboardingOnScreenResize() {
            if (window.innerWidth <= 900) {
                this.hideOnboardingOnMobile = true;

                onboardStatusState.dismiss();
            } else {
                this.hideOnboardingOnMobile = false;
            }
        },

        toggleMobileNavHandler() {
            this.mobileNavToggled = !this.mobileNavToggled;
        },

        updateNavShadow() {
            let amountScrolled = this.sideNavReference.clientHeight + this.sideNavReference.scrollTop;
            if(amountScrolled < this.sideNavReference.scrollHeight) {
                this.shadowedNav = true;
            } else {
                this.shadowedNav = false;
            }
        },
        
        resizeEventHandler() {
            /* --
            Keep 66ms throttle for a 15 frames per second refresh (1000/15)
            Keeps resize event performant while still feeling responsive.
            -- */
            const throttle = 66;
            if (!this.resizeEventThrottle) {
                this.updateNavShadow();
                this.windowWidth = window.innerWidth;
                this.resizeEventThrottle = true;
                setTimeout(()=>{
                    this.updateNavShadow();
                    this.resizeEventThrottle = false;
                    this.windowWidth = window.innerWidth;
                }, throttle);
            }  
        },

        subNavScrollHandler() {
            window.requestAnimationFrame(()=> {
                this.updateNavShadow();
            })
        },

        async orgSubNavToggleHandler() {
            if(!this.navCollapsedAuto) {
                const newExpandedStateValue = !this.organizationSubNavExpanded;
                const newExpandedStateString = newExpandedStateValue.toString();
                window.localStorage.setItem('dt-org-subnav-expanded', newExpandedStateString);
                this.organizationSubNavExpanded = newExpandedStateValue;
            }
        },

        async sigangeSubNavToggleHandler() {
            if(!this.navCollapsedAuto) {
                const newExpandedStateValue = !this.signageSubNavExpanded;
                const newExpandedStateString = newExpandedStateValue.toString();
                window.localStorage.setItem('dt-signage-subnav-expanded', newExpandedStateString);
                this.signageSubNavExpanded = newExpandedStateValue;
            }
        },

        async navCollapseHandler() {
            const currentCollapseState = this.navCollapsed;
            if(currentCollapseState == true) {
                window.localStorage.setItem('dt-nav-collapse', 'false');
                this.navCollapsed = false;
            } else {
                window.localStorage.setItem('dt-nav-collapse', 'true');
                this.navCollapsed = true;
            }
            await nextTick;
            this.updateNavShadow();
        },

        async receiverNavigationClick() {
            if(onboardStatusState && onboardStatusState.userOnboardStatus && onboardStatusState.userOnboardStatus.isOnboarding && onboardStatusState.userOnboardStatus.showDialog && this.currentStep.guideStepId == 'receiversClickReceivers') {
                await onboardStatusState.nextStep();
            }

            this.toggleMobileNavHandler();
        },
        async roomNavigationClick() {
            if(onboardStatusState && onboardStatusState.userOnboardStatus && onboardStatusState.userOnboardStatus.isOnboarding && onboardStatusState.userOnboardStatus.showDialog && this.currentStep.guideStepId == 'roomsClickRooms') {
                await onboardStatusState.nextStep();
            }

            this.toggleMobileNavHandler();
        },

        onReleaseClick() {
            // Update user to turn off notification dot
            if(this.me && this.me.showReleaseNotification) {
                this.me.showReleaseNotification = false;
                userState.updateMe(this.me);
            }
           
            // Open new tab to hubspot page
            window.open('https://www.airsquirrels.com/ditto-portal/updates', '_blank')?.focus();
        }
    }
});
